<template>
  <TemplateContentSubNav ref="pageContent" :has-header="false">
    <template #navigation>
      <DisplaySection gap="xl" tag="nav">
        <NavigationButtonLibraryNomad v-if="isAboveL" />
        <NavigationLibraryBooksSlider v-if="!isAboveL" variant="outlined" />
        <NavigationLibraryBooksList v-if="isAboveL" />
      </DisplaySection>
    </template>
    <AdventureNotAvailable
      class="page-adventure"
      :middle-img-src="`/images/cahier-de-vacances/cahier-de-vacances.webp`"
      :middle-img-srcset="`/images/cahier-de-vacances/cahier-de-vacances@2x.webp 2x`"
    >
      <template #description> {{ description }} </template>
      <div class="adventure-link">
        <img class="link-qr-code" :src="qrcode" alt="QR Code" />
        <div v-if="!mobile" class="separator">
          <span class="separator-deeplink">ou</span>
        </div>
        <ButtonBase
          v-if="mobile"
          theme="secondary"
          stroke="n"
          class="button"
          :disabled="false"
          rounded
          size="normal"
          :to="deeplink"
        >
          {{ $t('cahierdevacances.button.onMobile') }}
        </ButtonBase>
        <ButtonBase
          v-else
          theme="secondary"
          stroke="n"
          class="button"
          :disabled="false"
          rounded
          size="small"
          @click="copy(deeplink)"
        >
          {{ buttonLabel }}
        </ButtonBase>
      </div>
    </AdventureNotAvailable>
  </TemplateContentSubNav>
</template>
<script setup lang="ts">
import { useQRCode } from '@vueuse/integrations/useQRCode';
const profile = useProfileStore();
const context = useContextStore();
const { currentContent, ancestors, section } = inject(INJECT_CATEGORY_PAGE);
const { t } = useI18n();
const breakpoints = useEvaBreakpoints();
const isAboveL = breakpoints.greaterOrEqual('l');
const mobile = computed(() => (isAboveL.value ? 0 : 1));

const libraryBookId = computed(() => currentContent.value.id);

const breadcrumbLinks = useCategoryAncestorsLinks(
  ancestors,
  section.value,
  currentContent
);

const currentMemberToken = computed(() => {
  const currentMemberId = currentContent.value.memberId;
  const matchingMember = profile.familyMembers.find(
    (member) => member.memberId === currentMemberId
  );
  if (matchingMember) {
    const currentToken = matchingMember.loginToken;
    return currentToken;
  }
});
const parentToken = computed(() => {
  const parentId = context.memberId;
  const matchingMember = profile.familyMembers.find(
    (member) => member.memberId === parentId
  );
  if (matchingMember) {
    const token = matchingMember.loginToken;
    return token;
  }
});
const deeplink = computed(() => {
  if (!parentToken.value || !currentMemberToken.value) {
    return `https://nomadeducation.onelink.me/1B9T?pid=appShare&amp;deep_link_value=&af_web_dp=https://app.nomadeducation.fr`;
  }
  return `https://nomadeducation.onelink.me/1B9T?deep_link_value=nomadeducation-nomadeducation://nomadeducation.fr/tokenAuth/${currentMemberToken.value}?familyParentToken=${parentToken.value}&selectedUserContent=${libraryBookId.value}`;
});
const qrcode = useQRCode(deeplink.value);

useWebsiteHead({ title: breadcrumbLinks.map((l) => l.label).join(' - ') });

const pageContent = ref<HTMLElement>(null);

const description = computed(() => {
  if (!profile.isParent && !profile.isASubscriber) {
    return t('cahierdevacances.description.childAndNotSubscriber');
  } else if (profile.isParent && !profile.isASubscriber) {
    return t('cahierdevacances.description.parentAndNotSubscriber');
  } else {
    return t('cahierdevacances.description.parentAndSubscriber');
  }
});
const { copy, copied } = useClipboard();

const buttonLabel = computed(() => {
  return isShowingLabelCopy.value ? 'Copier le lien' : 'Copié';
});
const {
  start: startShowLabelCopy,
  ready: isShowingLabelCopy,
  stop,
} = useNomadTimeout(2000);

watch(
  () => copied.value,
  (bool) => {
    if (bool) {
      startShowLabelCopy();
    }
  }
);
</script>
<style lang="scss" scoped>
.page-adventure {
  .adventure-link {
    display: flex;
    flex-direction: column;
    justify-items: center;
    align-items: center;
    gap: var(--gap_s);
    .link-qr-code {
      display: none;
      @include above('l') {
        display: block;
        max-width: 20rem;
        border: 1px solid var(--color_neutral_200);
      }
    }
    .separator {
      display: flex;
      flex-direction: column;
      gap: var(--gap_s);
      width: 100%;
      &-deeplink {
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        align-items: center;
        gap: var(--gap_s);
        &::after,
        &::before {
          content: '';
          display: block;
          height: 0.1rem;
          transform: scaleX(1);
          background-color: var(--color_neutral_200);
        }
      }
    }
  }
}
</style>
